import type { ProfileSectionSlug } from '../types/profile';
import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export const ONBOARDING_STORAGE_KEY = 'lighthouse_candidate_onboarding';

export type CandidateOnboardingPayload = {
  fullName: string;
  headline: string;
  story: string;
  returner: boolean;
  registerAsTeam: boolean;
  teamValueProposition: string;
  teamReliability: string;
  teamAchievements: string;
  teamHistory: string;
  teamTenureYears: number;
  teamSize: number;
  teamMembers: Array<{
    fullName: string;
    headline: string;
    cvSummary: string;
    cvSkills: string[];
  }>;
  experienceLevel: 'early' | 'mid' | 'senior';
  skills: string[];
  focusAreas: string[];
  desiredRoles: string[];
  locationPreference: string;
  workStyle: 'remote' | 'hybrid' | 'onsite';
  salaryRange: string;
  availabilityDate: string;
  jobAlerts: boolean;
  alertFrequency: 'daily' | 'weekly' | 'off';
  notificationChannels: Array<'email' | 'sms' | 'in-app'>;
};

export type CandidateProfileResponse = {
  id?: string;
  onboardingCompleted?: boolean;
};

export type CandidateOnboardingStatusResponse = {
  onboardingCompleted: boolean;
};

export type CandidateProfile = Record<string, CandidateProfileSectionEntry[]>;

export type CandidateProfileSectionEntry = Record<string, string>;

export type CandidateProfileSection = {
  slug: string;
  entries: CandidateProfileSectionEntry[];
};

const PROFILE_STORAGE_KEY = 'lighthouse_candidate_profile';

const SECTION_ORDER: ProfileSectionSlug[] = [
  'personal-info',
  'work-experience',
  'education',
  'skills',
  'certifications',
  'portfolio-links',
];

const SECTION_TARGETS: Record<ProfileSectionSlug, number> = {
  'personal-info': 1,
  'work-experience': 2,
  education: 1,
  skills: 10,
  certifications: 1,
  'portfolio-links': 1,
};

const SECTION_LABELS: Record<ProfileSectionSlug, string> = {
  'personal-info': 'Personal Info',
  'work-experience': 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  certifications: 'Certifications',
  'portfolio-links': 'Portfolio & Links',
};

const SECTION_WEIGHTS: Record<ProfileSectionSlug, number> = {
  'personal-info': 0.18,
  'work-experience': 0.22,
  education: 0.1,
  skills: 0.22,
  certifications: 0.14,
  'portfolio-links': 0.14,
};

export type CandidateProfileSectionScore = {
  slug: ProfileSectionSlug;
  label: string;
  score: number;
  weight: number;
  completed: boolean;
  missingFields?: string[];
  highlights?: string[];
};

export type CandidateProfileRecommendation = {
  id: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  sectionSlug?: ProfileSectionSlug;
};

export type CandidateProfileBadge = {
  id: string;
  label: string;
  description: string;
  awardedAt: string;
  kind?: 'milestone' | 'returner' | 'ai';
};

export type CandidateProfileScore = {
  score: number;
  percentile: number;
  level: 'emerging' | 'growing' | 'strong' | 'excellent';
  summary: string;
  lastUpdated: string;
  profile: {
    fullName: string;
    headline: string;
    location: string;
    availability: string;
    yearsExperience: number;
    focusRoles: string[];
    skills: string[];
  };
  sections: CandidateProfileSectionScore[];
  recommendations: CandidateProfileRecommendation[];
  badges: CandidateProfileBadge[];
};

export type CandidateProfileExportResult = {
  blob: Blob;
  fileName: string;
  mimeType: string;
};

export async function saveCandidateOnboarding(
  payload: CandidateOnboardingPayload,
): Promise<CandidateProfileResponse> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to save candidate onboarding.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile/onboarding`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Onboarding save failed (${response.status}): ${errorText}`);
  }

  const data = (await response.json()) as CandidateProfileResponse;
  return data ?? { onboardingCompleted: true };
}

export async function fetchCandidateOnboardingDraft(): Promise<Partial<CandidateOnboardingPayload> | undefined> {
  if (!API_BASE_URL) {
    if (typeof window === 'undefined') return undefined;
    const raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as Partial<CandidateOnboardingPayload>;
    } catch {
      return undefined;
    }
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile/onboarding`, {
    headers: { 'Content-Type': 'application/json' },
  });

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Onboarding draft fetch failed (${response.status}): ${errorText}`);
  }

  const payload = (await response.json()) as CandidateOnboardingPayload;
  return payload ?? undefined;
}

export async function fetchCandidateOnboardingStatus(): Promise<CandidateOnboardingStatusResponse> {
  if (!API_BASE_URL) {
    if (typeof window === 'undefined') {
      return { onboardingCompleted: true };
    }
    const exists = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    return { onboardingCompleted: !exists };
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile/onboarding/status`, {
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Onboarding status fetch failed (${response.status})`);
  }

  return (await response.json()) as CandidateOnboardingStatusResponse;
}

export async function fetchCandidateProfile(): Promise<CandidateProfile> {
  if (!API_BASE_URL) {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as CandidateProfile;
    } catch {
      return {};
    }
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Candidate profile fetch failed (${response.status})`);
  }
  const payload = (await response.json()) as CandidateProfile;
  return payload ?? {};
}

export async function updateCandidateProfileSection(
  slug: string,
  entries: CandidateProfileSectionEntry[],
): Promise<CandidateProfileSection> {
  if (!API_BASE_URL) {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem(PROFILE_STORAGE_KEY);
      const data = raw ? (JSON.parse(raw) as CandidateProfile) : {};
      data[slug] = entries;
      window.localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(data));
    }
    return { slug, entries };
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile/sections/${slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entries }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Section update failed (${response.status}): ${errorText}`);
  }

  const payload = (await response.json()) as CandidateProfileSection;
  return payload ?? { slug, entries };
}

function deriveScoreLevel(score: number): CandidateProfileScore['level'] {
  if (score >= 90) return 'excellent';
  if (score >= 75) return 'strong';
  if (score >= 60) return 'growing';
  return 'emerging';
}

const ENTRY_LABEL_FIELDS = ['name', 'title', 'role', 'position', 'company', 'school', 'institution', 'label', 'value', 'skill'];

function getEntryLabel(entry: CandidateProfileSectionEntry): string | undefined {
  for (const field of ENTRY_LABEL_FIELDS) {
    if (entry[field]) {
      return entry[field];
    }
  }
  return undefined;
}

function normalizeArrayField(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function deriveProfileBasics(profile: CandidateProfile): CandidateProfileScore['profile'] {
  const personalInfo = (profile['personal-info'] ?? [])[0] ?? {};
  const skillsEntries = (profile.skills ?? []) as CandidateProfileSectionEntry[];
  const skillNames = skillsEntries.map(getEntryLabel).filter(Boolean) as string[];
  const yearsExperienceRaw = personalInfo.yearsExperience ?? personalInfo.experienceYears;
  const yearsExperience = yearsExperienceRaw ? Number.parseInt(yearsExperienceRaw, 10) || 0 : skillNames.length >= 10 ? 8 : 3;
  return {
    fullName: personalInfo.fullName || 'Candidate',
    headline: personalInfo.headline || personalInfo.summary || 'Add a headline to stand out.',
    location: personalInfo.location || 'Location not provided',
    availability: personalInfo.availability || 'Not specified',
    yearsExperience,
    focusRoles: normalizeArrayField(personalInfo.desiredRoles).slice(0, 3),
    skills: skillNames.slice(0, 8),
  };
}

function buildSectionHighlights(slug: ProfileSectionSlug, entries: CandidateProfileSectionEntry[]) {
  if (!entries.length) return undefined;
  if (slug === 'skills') {
    return entries
      .slice(0, 4)
      .map(getEntryLabel)
      .filter((value): value is string => Boolean(value));
  }
  if (slug === 'work-experience') {
    return entries.slice(0, 2).map((entry) => {
      const role = entry.role ?? entry.title ?? entry.position ?? 'Role';
      const company = entry.company ?? entry.employer ?? '';
      return company ? `${role} · ${company}` : role;
    });
  }
  if (slug === 'education') {
    return entries
      .slice(0, 2)
      .map((entry) => entry.school ?? entry.institution ?? entry.title)
      .filter((value): value is string => Boolean(value));
  }
  return undefined;
}

function buildMissingFields(slug: ProfileSectionSlug, entries: CandidateProfileSectionEntry[], target: number) {
  if (entries.length >= target) return undefined;
  if (entries.length === 0) {
    if (slug === 'portfolio-links') return ['Link to your portfolio or presentations'];
    if (slug === 'work-experience') return ['Add at least one role'];
    return ['Add details to this section'];
  }
  const remaining = Math.max(0, target - entries.length);
  return remaining > 0 ? [`Add ${remaining} more entr${remaining === 1 ? 'y' : 'ies'}`] : undefined;
}

function buildFallbackRecommendations(
  profile: CandidateProfile,
  sections: CandidateProfileSectionScore[],
): CandidateProfileRecommendation[] {
  const personalInfo = (profile['personal-info'] ?? [])[0] ?? {};
  const items: CandidateProfileRecommendation[] = [];
  if (!personalInfo.resumeUrl) {
    items.push({
      id: 'upload-cv',
      title: 'Upload your CV',
      description: 'Applications with attached CVs get reviewed 2x faster.',
      impact: 'high',
      sectionSlug: 'personal-info',
    });
  }
  sections.forEach((section) => {
    if (!section.completed) {
      items.push({
        id: `complete-${section.slug}`,
        title: `Complete ${section.label}`,
        description:
          section.slug === 'skills'
            ? 'Add at least 10 skills to boost your match score.'
            : section.slug === 'work-experience'
            ? 'Share recent achievements to prove your impact.'
            : section.slug === 'portfolio-links'
            ? 'Link to presentations, repos, or public speaking clips.'
            : 'Fill in this section to strengthen your profile.',
        impact: section.slug === 'skills' || section.slug === 'work-experience' ? 'high' : 'medium',
        sectionSlug: section.slug,
      });
    }
  });
  return items.slice(0, 4);
}

function buildFallbackBadges(
  sections: CandidateProfileSectionScore[],
  score: number,
): CandidateProfileBadge[] {
  const issued = new Date().toISOString();
  const badgesList: CandidateProfileBadge[] = [];
  if (score >= 80) {
    badgesList.push({
      id: 'profile-ready',
      label: 'Profile ready',
      description: 'Your core sections are complete and ready for employers.',
      awardedAt: issued,
      kind: 'milestone',
    });
  }
  const workExperience = sections.find((section) => section.slug === 'work-experience');
  if (workExperience?.completed) {
    badgesList.push({
      id: 'storyteller',
      label: 'Impact storyteller',
      description: 'You documented measurable achievements in your roles.',
      awardedAt: issued,
      kind: 'ai',
    });
  }
  const skills = sections.find((section) => section.slug === 'skills');
  if (skills?.score && skills.score >= 90) {
    badgesList.push({
      id: 'skill-pro',
      label: 'Skills powerhouse',
      description: 'A broad skill set helps you rank in more searches.',
      awardedAt: issued,
      kind: 'milestone',
    });
  }
  return badgesList;
}

function deriveScoreFromProfile(profile: CandidateProfile): CandidateProfileScore {
  const now = new Date().toISOString();
  const sections = SECTION_ORDER.map((slug) => {
    const entries = (profile[slug] ?? []) as CandidateProfileSectionEntry[];
    const target = SECTION_TARGETS[slug] ?? 1;
    const score =
      target === 0 ? 100 : Math.min(100, Math.round((entries.length / Math.max(target, 1)) * 100));
    return {
      slug,
      label: SECTION_LABELS[slug],
      score,
      weight: SECTION_WEIGHTS[slug] ?? 0.15,
      completed: entries.length >= Math.min(target, 1),
      missingFields: buildMissingFields(slug, entries, target),
      highlights: buildSectionHighlights(slug, entries),
    };
  });
  const totalWeight = sections.reduce((sum, section) => sum + section.weight, 0) || 1;
  const weightedScore = Math.round(
    sections.reduce((sum, section) => sum + section.score * section.weight, 0) / totalWeight,
  );
  const level = deriveScoreLevel(weightedScore);
  const percentile = Math.min(95, Math.max(10, Math.round(weightedScore * 0.85)));
  const summary =
    level === 'excellent'
      ? 'Your profile is market-ready. Keep it fresh to stay visible.'
      : level === 'strong'
      ? 'Great job! Filling remaining sections unlocks premium matches.'
      : level === 'growing'
      ? 'Complete missing sections to surface to more employers.'
      : 'Add the fundamentals so Lighthouse can champion your story.';
  return {
    score: weightedScore,
    percentile,
    level,
    summary,
    lastUpdated: now,
    profile: deriveProfileBasics(profile),
    sections,
    recommendations: buildFallbackRecommendations(profile, sections),
    badges: buildFallbackBadges(sections, weightedScore),
  };
}

export async function fetchCandidateProfileScore(): Promise<CandidateProfileScore> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load candidate profile score.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile/score`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Candidate profile score fetch failed (${response.status})`);
  }
  const payload = (await response.json()) as CandidateProfileScore;
  if (!payload || typeof payload.score !== 'number') {
    throw new Error('Candidate profile score response malformed');
  }
  return payload;
}

export async function exportCandidateProfilePdf(): Promise<CandidateProfileExportResult> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to export the candidate profile.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/candidate-profile/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format: 'pdf' }),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Profile export failed (${response.status}): ${errorText}`);
  }
  const blob = await response.blob();
  return {
    blob,
    fileName: 'lighthouse-profile.pdf',
    mimeType: blob.type || 'application/pdf',
  };
}
